import { onBeforeUnmount, onMounted, ref } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';

export const useUnsavedChangesGuard = ({
  assistantId,
  hasUnsavedChanges,
  refresh,
  discardDraft,
}) => {
  const router = useRouter();
  const pendingNavigation = ref(null);
  const pendingRefresh = ref(false);
  const allowNavigation = ref(false);

  const requestRefresh = () => {
    if (!hasUnsavedChanges.value) {
      refresh();
      return;
    }
    pendingRefresh.value = true;
  };

  const queueNavigation = to => {
    if (allowNavigation.value || !hasUnsavedChanges.value) return true;
    pendingNavigation.value = to.fullPath;
    return false;
  };

  const closeDialog = () => {
    pendingNavigation.value = null;
    pendingRefresh.value = false;
  };

  const discardChanges = async () => {
    const targetPath = pendingNavigation.value;
    const shouldRefresh = pendingRefresh.value;
    closeDialog();
    allowNavigation.value = true;
    try {
      if (shouldRefresh) {
        discardDraft();
        await refresh();
      } else if (targetPath) {
        await router.push(targetPath);
      }
    } finally {
      allowNavigation.value = false;
    }
  };

  const handleBeforeUnload = event => {
    if (!hasUnsavedChanges.value) return;
    event.preventDefault();
    event.returnValue = '';
  };

  onBeforeRouteUpdate(to => {
    if (String(to.params.assistantId) === String(assistantId.value))
      return true;
    return queueNavigation(to);
  });
  onBeforeRouteLeave(to => queueNavigation(to));
  onMounted(() => window.addEventListener('beforeunload', handleBeforeUnload));
  onBeforeUnmount(() =>
    window.removeEventListener('beforeunload', handleBeforeUnload)
  );

  return {
    pendingNavigation,
    pendingRefresh,
    requestRefresh,
    closeUnsavedChangesDialog: closeDialog,
    discardUnsavedChanges: discardChanges,
  };
};
